// Web's copy of mobile/src/footage.ts: the two projects share no code, and
// mobile/src/ruleParity.test.ts holds them to the same behaviour.
import type { ClipPart } from './api'

export interface FootageRun {
  id: number
  // Index into the parts list of the run's first file.
  first: number
  start: number
  end: number
  seconds: number
  // Position along the bar, as fractions of the footage's whole span.
  left: number
  width: number
}

export interface FootageLayout {
  runs: FootageRun[]
  // Seconds never written between one run and the next, in order.
  gaps: number[]
  missing: number
  // Where the incident's moment falls along the bar, clamped to it.
  moment: number
  start: number
  end: number
}

// A recorder writes a file per trigger, not per stretch of footage — one
// measured incident returned 122 files covering three continuous runs. What a
// dispatcher counts, and what a gap sits between, is the run. An older server
// names no run, and every file is then its own.
export const stretchOf = (p: ClipPart) => p.stretch ?? p.index

// footageLayout draws an incident's footage as runs along a bar spanning the
// first recorded second to the last.
export function footageLayout(parts: ClipPart[], moment: number): FootageLayout {
  const byRun = new Map<number, { first: number; start: number; end: number }>()
  parts.forEach((p, i) => {
    const run = byRun.get(stretchOf(p))
    if (!run) byRun.set(stretchOf(p), { first: i, start: p.start, end: p.end })
    else {
      run.start = Math.min(run.start, p.start)
      run.end = Math.max(run.end, p.end)
    }
  })
  const spans = [...byRun.entries()].map(([id, r]) => ({ id, ...r }))
  if (spans.length === 0) return { runs: [], gaps: [], missing: 0, moment: 0, start: 0, end: 0 }

  const start = Math.min(...spans.map((r) => r.start))
  const end = Math.max(...spans.map((r) => r.end))
  const length = end - start || 1
  const at = (t: number) => Math.round(((t - start) / length) * 1000) / 1000
  const runs = spans.map((r) => ({
    ...r,
    seconds: Math.round(r.end - r.start),
    left: at(r.start),
    width: Math.round(((r.end - r.start) / length) * 1000) / 1000,
  }))
  const gaps = runs.slice(1).map((r, i) => Math.max(0, Math.round(r.start - runs[i].end)))
  return {
    runs,
    gaps,
    missing: gaps.reduce((a, b) => a + b, 0),
    moment: Math.min(1, Math.max(0, at(moment))),
    start,
    end,
  }
}

// Opening is which file to play and whether from its own start; otherwise
// playback opens shortly before the incident's moment.
export interface Opening {
  at: number
  fromStart: boolean
}

// openingPart is where a clip opens: the file holding the incident's moment.
// When none does — the moment fell in a gap or a seam, or outside the footage
// through a camera's clock drifting — it is the first file after the moment,
// or the last file when none is, from its start (#272). Falling back to the first file
// while still measuring from the moment seeked past its end, and playback ran
// through every file the same way.
export function openingPart(parts: ClipPart[], moment: number): Opening {
  const holding = parts.findIndex((p) => moment >= p.start && moment <= p.end)
  if (holding >= 0) return { at: holding, fromStart: false }
  const after = parts.findIndex((p) => p.start > moment)
  return { at: after >= 0 ? after : Math.max(0, parts.length - 1), fromStart: true }
}

// pickRun is where a recording picked from the bar plays from: the file in it
// holding the moment, or its first file from the start when none does — a run
// that does not reach the moment, or a moment in the seam between two of its
// files (#271).
export function pickRun(parts: ClipPart[], run: number, moment: number): Opening {
  const first = Math.max(0, parts.findIndex((p) => stretchOf(p) === run))
  const own = parts.findIndex((p, i) => i >= first && stretchOf(p) === run && moment >= p.start && moment <= p.end)
  return own >= 0 ? { at: own, fromStart: false } : { at: first, fromStart: true }
}

// advance is where playback runs on to when a file ends: the next file, from
// its start, whatever the moment. Measured from the moment, a file wholly
// before it was sought past its end, so a run picked from the bar skipped
// every file after its first (#287). playFrom skips the overlap with the
// file just seen.
export function advance(parts: ClipPart[], at: number): Opening | null {
  return at < parts.length - 1 ? { at: at + 1, fromStart: true } : null
}

// playFrom is the second a file starts playing at. Opening on the incident, it
// is shortly before the moment; a file picked from the bar plays from its own
// start. Running on into the next file skips the seconds the two share — the
// recorder opens each file before the last has finished — so they are not
// played twice. Here rather than in each player, which drifted apart (#289).
export function playFrom(parts: ClipPart[], at: number, moment: number, how: { fromStart: boolean; ranOn: boolean; leadIn: number }): number {
  const part = parts[at]
  if (!part) return 0
  const overlapIn = how.ranOn && at > 0 && parts[at - 1].end > part.start ? parts[at - 1].end - part.start : 0
  return how.fromStart ? overlapIn : Math.max(overlapIn, moment - part.start - how.leadIn)
}
